import { useCallback } from 'react';
import Vec2 from 'vec2';

import { UserDesignOperation } from '~/core/enums';
import { DrawingTabEnum } from '~/core/enums/drawing-tab.enum';
import { updateShapeByTranslation } from '~/core/helpers';
import useAppliance, { ApplianceMoveResult } from '~/core/hooks/useAppliance';
import { useRegisterUserAction } from '~/core/hooks/useRegisterUserAction';
import { DrawingShape } from '~/core/models';
import {
  useDrawingShapeContext,
  useFabricCanvasContext,
} from '~/legacy/easy-estimator-page/context';
import { useAppDispatch } from '~/redux/hooks';
import {
  setPreventRepricing,
  updateNeedRedrawCanvas,
} from '~/redux/slices/drawingShape';
import { updateSelectedDrawingTab } from '~/redux/slices/shape';

export const useMouseUp = () => {
  const dispatch = useAppDispatch();

  const {
    getters: { numberOfShapesInTheDrawingArea },
    handlers: { updateDrawingShapes },
  } = useDrawingShapeContext();

  const { drawingShapesRef, movingItemRef } = useFabricCanvasContext();

  const { updateApplianceByTranslation } = useAppliance();
  const { registerUserAction } = useRegisterUserAction();

  const handleShapeMovement = useCallback(
    (moveVec: Vec2) => {
      if (numberOfShapesInTheDrawingArea > 1) {
        registerUserAction(UserDesignOperation.MOVE_SHAPE);
      }
      dispatch(setPreventRepricing(true));
      const shape = movingItemRef.current as DrawingShape;

      updateShapeByTranslation(moveVec, shape);
      updateDrawingShapes([...drawingShapesRef.current]);
    },
    [
      dispatch,
      drawingShapesRef,
      movingItemRef,
      numberOfShapesInTheDrawingArea,
      registerUserAction,
      updateDrawingShapes,
    ]
  );

  const handleApplianceMovement = useCallback(
    (moveVec: Vec2): ApplianceMoveResult => {
      // moving an already selected appliance
      dispatch(setPreventRepricing(true));

      const result = updateApplianceByTranslation(
        moveVec,
        movingItemRef.current as string,
        drawingShapesRef.current
      );

      // NOTE: We intentionally DON'T call updateDrawingShapes here!
      // The caller (onMouseUp) must call updateActiveDrawingAppliance(undefined) FIRST,
      // then call syncShapesToContext() to trigger the redraw.
      // This ensures the redraw sees activeDrawingApplianceId = undefined.

      // Only register user action if the move was successful
      if (result.success) {
        registerUserAction(UserDesignOperation.MOVE_APPLIANCE);
      }

      return result;
    },
    [
      dispatch,
      drawingShapesRef,
      movingItemRef,
      registerUserAction,
      updateApplianceByTranslation,
    ]
  );

  // Sync modified shapes to context - call AFTER clearing activeDrawingApplianceId
  const syncShapesToContext = useCallback(() => {
    updateDrawingShapes([...drawingShapesRef.current]);
  }, [drawingShapesRef, updateDrawingShapes]);

  const finalizeDraggingState = useCallback(() => {
    dispatch(updateSelectedDrawingTab(DrawingTabEnum.SELECTED_SHAPE_TAB));

    // FIX: Force the redraw effect to trigger by ensuring state actually changes.
    // If needRedrawCanvas is already true, setting it to true again won't trigger
    // the effect. By first setting to false, we guarantee a state change.
    dispatch(updateNeedRedrawCanvas(false));
    queueMicrotask(() => {
      dispatch(updateNeedRedrawCanvas(true));
    });

    movingItemRef.current = undefined;
  }, [dispatch, movingItemRef]);

  return {
    finalizeDraggingState,
    handleShapeMovement,
    handleApplianceMovement,
    syncShapesToContext,
  };
};
